<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { useFavoriteInboxes } from 'dashboard/composables/useFavoriteInboxes';
import { useFavoriteLabels } from 'dashboard/composables/useFavoriteLabels';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import IxcInvoiceNotificationDeliveriesAPI from 'dashboard/api/ixcInvoiceNotificationDeliveries';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import SidebarCustomizationDialog from './SidebarCustomizationDialog.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';
import {
  excludeFavoriteMenuItems,
  orderSidebarSections,
  resolveFavoriteMenuItems,
  uniqueSidebarNames,
} from 'dashboard/helper/sidebarCustomization';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnTalkviaCloud } = useAccount();
const { isEnterprise } = useConfig();
const store = useStore();
const { t } = useI18n();
const talkviaBrand = {
  shortName: 'T',
  name: 'Talkvia',
  tagline: 'Comunicação inteligente',
};

const isCallsAvailable = computed(() => isOnTalkviaCloud.value || isEnterprise);

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const isAdmin = computed(
  () => isSuperAdmin.value || currentRole.value === 'administrator'
);
const canAccessTalkviaShop = computed(
  () => isSuperAdmin.value || currentRole.value === 'administrator'
);
const accountId = useMapGetter('getCurrentAccountId');
const hasActiveIxcIntegration = ref(false);
const currentAccountMembership = computed(() =>
  currentUser.value?.accounts?.find(
    account => Number(account.id) === Number(accountId.value)
  )
);
const canAccessLemit = computed(
  () => isAdmin.value || currentAccountMembership.value?.lemit_access
);
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const hasFilteredUnreadCounts = computed(() => {
  return (
    hasConversationUnreadCounts.value &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS
    )
  );
});

const hasAccountFeature = feature =>
  isFeatureEnabledonAccount.value(accountId.value, feature);

const hasFeature = feature => isSuperAdmin.value || hasAccountFeature(feature);

const hasCorbanFactaOffline = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE)
);

const hasCorbanCltFgtsQuery = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY)
);

const hasCorbanCreditEngine = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE)
);

const hasCorbanManualSimulators = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS)
);

const hasCorbanProposalPipeline = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE)
);

const hasLogisticsInventoryRoutes = computed(() =>
  hasAccountFeature(FEATURE_FLAGS.TALKVIA_LOGISTICS_INVENTORY_ROUTES)
);

const hasCorbanModule = computed(
  () =>
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE)
);

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const fetchIxcIntegrationStatus = async currentAccountId => {
  hasActiveIxcIntegration.value = false;
  if (!currentAccountId) return;

  try {
    const { data } = await IxcInvoiceNotificationDeliveriesAPI.status();
    if (Number(accountId.value) === Number(currentAccountId)) {
      hasActiveIxcIntegration.value = data?.enabled === true;
    }
  } catch {
    hasActiveIxcIntegration.value = false;
  }
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

const toggleSidebarCollapse = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const labels = useMapGetter('labels/getLabelsOnSidebar');
const inboxes = useMapGetter('inboxes/getInboxes');
const hasWhatsappCloudInbox = computed(() =>
  inboxes.value.some(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);
const { favoriteInboxIds } = useFavoriteInboxes();
const { favoriteLabelIds } = useFavoriteLabels();
const favoriteInboxes = computed(() => {
  const inboxesById = new Map(
    inboxes.value.map(inbox => [Number(inbox.id), inbox])
  );
  return favoriteInboxIds.value
    .map(inboxId => inboxesById.get(inboxId))
    .filter(Boolean);
});
const favoriteLabels = computed(() => {
  const labelsById = new Map(
    labels.value.map(label => [Number(label.id), label])
  );

  return favoriteLabelIds.value
    .map(labelId => labelsById.get(labelId))
    .filter(Boolean);
});
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const mentionsUnreadCount = useMapGetter(
  'conversationUnreadCounts/getMentionsUnreadCount'
);
const participatingUnreadCount = useMapGetter(
  'conversationUnreadCounts/getParticipatingUnreadCount'
);
const unattendedUnreadCount = useMapGetter(
  'conversationUnreadCounts/getUnattendedUnreadCount'
);
const getFolderUnreadCount = useMapGetter(
  'conversationUnreadCounts/getFolderUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

onMounted(() => {
  // The sidebar must not disappear when the browser cache/IndexedDB stalls.
  store.dispatch('labels/getFromNetwork');
  store.dispatch('inboxes/getFromNetwork');
  store.dispatch('conversationOpenCounts/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

watch(accountId, fetchIxcIntegrationStatus, { immediate: true });

const hasUnreadCountsForSection = section => {
  if (section === SIDEBAR_SORT_SECTIONS.FOLDERS) {
    return hasFilteredUnreadCounts.value;
  }

  return hasConversationUnreadCounts.value;
};

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
    unreadCountKey: view => getFolderUnreadCount.value(view.id),
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

const unreadBadgeTooltips = computed(() => ({
  ALL: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.ALL'),
  INBOX: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.INBOX'),
  LABEL: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.LABEL'),
  TEAM: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.TEAM'),
  FOLDER: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.FOLDER'),
  MENTIONS: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.MENTIONS'),
  PARTICIPATING: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.PARTICIPATING'),
  UNATTENDED: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.UNATTENDED'),
}));

const unreadBadgeTooltip = type => unreadBadgeTooltips.value[type];

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());
const menuSection = (label, options = {}) => ({
  type: 'section',
  name: `section-${label}`,
  label,
  show: options.show ?? true,
});

const menuItems = computed(() => {
  return [
    {
      name: 'Home',
      label: 'Home',
      icon: 'i-lucide-house',
      activeOn: ['home'],
      to: accountScopedRoute('home'),
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-inbox',
          badgeCount: allUnreadCount.value,
          badgeTooltip: unreadBadgeTooltip('ALL'),
          activeOn: ['talkvia_conversations', 'inbox_conversation'],
          to: accountScopedRoute('talkvia_conversations'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-lucide-at-sign',
          badgeCount: hasFilteredUnreadCounts.value
            ? mentionsUnreadCount.value
            : 0,
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          icon: 'i-lucide-user-round-check',
          badgeCount: hasFilteredUnreadCounts.value
            ? participatingUnreadCount.value
            : 0,
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          icon: 'i-lucide-clock-alert',
          badgeCount: hasFilteredUnreadCounts.value
            ? unattendedUnreadCount.value
            : 0,
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'AI Queue',
          label: 'Atendimento IA',
          icon: 'i-lucide-bot',
          activeOn: ['conversation_ai', 'conversation_through_ai'],
          to: accountScopedRoute('conversation_ai'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            badgeCount: hasFilteredUnreadCounts.value
              ? getFolderUnreadCount.value(view.id)
              : 0,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          to: accountScopedRoute('conversation_filters', {
            filterType: 'inboxes',
          }),
        },
        ...favoriteInboxes.value.map(inbox => ({
          name: `Favorite inbox-${inbox.id}`,
          label: inbox.name,
          icon: h(ChannelIcon, {
            inbox,
            colored: true,
            useBrandIcon: true,
            class: 'size-4',
          }),
          class: 'ms-7',
          to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
          activeOn: ['inbox_dashboard'],
        })),
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          to: accountScopedRoute('conversation_filters', {
            filterType: 'labels',
          }),
        },
        ...favoriteLabels.value.map(label => ({
          name: `Favorite label-${label.id}`,
          label: label.title,
          icon: h('span', {
            class: 'h-4 w-1 rounded-full',
            style: { backgroundColor: label.color },
          }),
          class: 'ms-7',
          to: accountScopedRoute('label_conversations', {
            label: label.title,
          }),
          activeOn: ['label_conversations', 'conversations_through_label'],
        })),
      ],
    },
    {
      name: 'Notifications',
      label: t('SIDEBAR.NOTIFICATIONS'),
      icon: 'i-lucide-bell',
      activeOn: ['notifications_index'],
      to: accountScopedRoute('notifications_index'),
      getterKeys: {
        badge: 'notifications/getHasUnreadNotifications',
        count: 'notifications/getUnreadCount',
      },
    },
    menuSection('Corban', { show: hasCorbanModule.value }),
    {
      name: 'Pulso CLT',
      label: 'Pulso CLT',
      icon: 'i-lucide-activity',
      activeOn: ['talkvia_credit_dashboard'],
      to: accountScopedRoute('talkvia_credit_dashboard'),
      show: isAdmin.value && hasCorbanModule.value,
    },
    {
      name: 'Motores de crédito',
      label: 'Motores de crédito',
      icon: 'i-lucide-landmark',
      activeOn: ['talkvia_credit_engines'],
      to: accountScopedRoute('talkvia_credit_engines'),
      show: isSuperAdmin.value && hasCorbanCreditEngine.value,
    },
    {
      name: 'Simuladores manuais',
      label: 'Simuladores manuais',
      icon: 'i-lucide-calculator',
      activeOn: ['talkvia_manual_simulators'],
      to: accountScopedRoute('talkvia_manual_simulators'),
      show: isSuperAdmin.value && hasCorbanManualSimulators.value,
    },
    {
      name: 'Facta offline',
      label: 'Facta offline',
      icon: 'i-lucide-file-spreadsheet',
      activeOn: ['talkvia_facta_offline_batches'],
      to: accountScopedRoute('talkvia_facta_offline_batches'),
      show: isAdmin.value && hasCorbanFactaOffline.value,
    },
    {
      name: 'Funil visual',
      label: 'Funil visual',
      icon: 'i-lucide-workflow',
      activeOn: ['talkvia_funnels'],
      to: accountScopedRoute('talkvia_funnels'),
      show: isSuperAdmin.value,
    },
    {
      name: 'Consulta CLT',
      label: 'Consulta CLT',
      icon: 'i-lucide-search-check',
      activeOn: ['talkvia_connected_bank_queries'],
      to: accountScopedRoute('talkvia_connected_bank_queries'),
      show: isAdmin.value && hasCorbanCltFgtsQuery.value,
    },
    {
      name: 'Esteira de propostas',
      label: 'Esteira de propostas',
      icon: 'i-lucide-panel-top',
      activeOn: ['talkvia_proposals'],
      to: accountScopedRoute('talkvia_proposals'),
      show: hasCorbanProposalPipeline.value,
    },
    menuSection('Operação'),
    {
      name: 'Gestão de carteira',
      label: 'Gestão de carteira',
      icon: 'i-lucide-users-round',
      activeOn: ['talkvia_portfolio_management'],
      to: accountScopedRoute('talkvia_portfolio_management'),
      show: isAdmin.value,
    },
    {
      name: 'Kanban',
      label: 'Kanban',
      icon: 'i-lucide-kanban',
      activeOn: ['conversation_kanban'],
      to: accountScopedRoute('conversation_kanban'),
    },
    {
      name: 'Calls',
      label: t('SIDEBAR.CALLS'),
      icon: 'i-lucide-phone',
      to: accountScopedRoute('calls_dashboard_index'),
      activeOn: ['calls_dashboard_index'],
      show: isCallsAvailable.value,
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Novidades',
      label: 'Novidades',
      icon: 'i-lucide-bell-ring',
      activeOn: ['platform_announcements'],
      to: accountScopedRoute('platform_announcements'),
    },
    {
      name: 'Contratos',
      label: 'Contratos',
      icon: 'i-lucide-file-signature',
      activeOn: ['talkvia_contracts'],
      to: accountScopedRoute('talkvia_contracts'),
    },
    {
      name: 'Captação WhatsApp',
      label: 'Captação WhatsApp',
      icon: 'i-lucide-message-circle-heart',
      activeOn: ['qualifica_ai'],
      to: accountScopedRoute('qualifica_ai'),
      show: isSuperAdmin.value,
    },
    {
      name: 'Agenda',
      label: 'Agenda',
      icon: 'i-lucide-calendar-days',
      activeOn: ['talkvia_calendar'],
      to: accountScopedRoute('talkvia_calendar'),
    },
    {
      name: 'Estoque',
      label: 'Estoque',
      icon: 'i-lucide-package',
      activeOn: ['talkvia_inventory'],
      to: accountScopedRoute('talkvia_inventory'),
      show: hasLogisticsInventoryRoutes.value,
    },
    {
      name: 'Rotas',
      label: 'Rotas',
      icon: 'i-lucide-route',
      activeOn: ['talkvia_delivery_routes'],
      to: accountScopedRoute('talkvia_delivery_routes'),
      show: hasLogisticsInventoryRoutes.value,
    },
    {
      name: 'Marketplace',
      label: 'Marketplace',
      icon: 'i-lucide-shopping-bag',
      activeOn: ['talkvia_shop'],
      to: accountScopedRoute('talkvia_shop'),
      show: canAccessTalkviaShop.value,
    },
    {
      name: 'Indique e ganhe',
      label: 'Indique e ganhe',
      icon: 'i-lucide-gift',
      activeOn: ['talkvia_referrals'],
      to: accountScopedRoute('talkvia_referrals'),
      show: canAccessTalkviaShop.value,
    },
    {
      name: 'Higienização',
      label: 'Higienização',
      icon: 'i-lucide-contact-round',
      activeOn: ['talkvia_lemit'],
      to: accountScopedRoute('talkvia_lemit'),
      show: canAccessLemit.value,
    },
    menuSection('Automação e conteúdo'),
    {
      name: 'SMS',
      label: 'Disparos SMS',
      icon: 'i-lucide-message-square-share',
      activeOn: ['talkvia_sms'],
      to: accountScopedRoute('talkvia_sms'),
    },
    {
      name: 'Integrations',
      label: t('SIDEBAR.INTEGRATIONS'),
      icon: 'i-lucide-plug',
      activeOn: [
        'settings_applications',
        'settings_integrations_dashboard_apps',
        'settings_integrations_webhook',
        'settings_integrations_slack',
        'settings_integrations_linear',
        'settings_integrations_notion',
        'settings_integrations_shopify',
        'settings_integrations_google_calendar',
        'settings_integrations_banking',
        'settings_integrations_erp',
        'settings_applications_integration',
      ],
      to: accountScopedRoute('settings_applications'),
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Biblioteca de IA',
      label: 'Biblioteca de IA',
      icon: 'i-lucide-bot',
      activeOn: ['talkvia_openai'],
      to: accountScopedRoute('talkvia_openai'),
      show: isAdmin.value,
    },
    {
      name: 'Supervisão IA',
      label: 'Supervisão IA',
      icon: 'i-lucide-radar',
      activeOn: ['talkvia_ai_supervision'],
      to: accountScopedRoute('talkvia_ai_supervision'),
      show: isSuperAdmin.value,
    },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      activeOn: ['captain_assistants_create_index'],
      children: [
        {
          name: 'Overview',
          label: t('SIDEBAR.CAPTAIN_OVERVIEW'),
          activeOn: ['captain_assistants_overview_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_overview_index',
          }),
        },
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: [
            'captain_assistants_responses_index',
            'captain_assistants_faq_suggestions',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          activeOn: ['captain_assistants_documents_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_documents_index',
          }),
        },
        {
          name: 'Scenarios',
          label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
          activeOn: ['captain_assistants_scenarios_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_scenarios_index',
          }),
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.CAPTAIN_INBOXES'),
          activeOn: ['captain_assistants_inboxes_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_inboxes_index',
          }),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.CAPTAIN_SETTINGS'),
          activeOn: [
            'captain_assistants_settings_index',
            'captain_assistants_guidelines_index',
            'captain_assistants_guardrails_index',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Tour Talkvia',
      label: 'Manual e Tour',
      icon: 'i-lucide-map',
      activeOn: ['talkvia_tour'],
      to: accountScopedRoute('talkvia_tour'),
    },
    menuSection('Gestão'),
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports AI Usage',
          label: 'Consumo de IA',
          to: accountScopedRoute('ai_usage_reports'),
        },
        {
          name: 'Reports Agent Pauses',
          label: t('SIDEBAR.REPORTS_AGENT_PAUSES'),
          to: accountScopedRoute('agent_pause_reports'),
        },
        ...(hasActiveIxcIntegration.value
          ? [
              {
                name: 'Reports IXC',
                label: t('SIDEBAR.REPORTS_IXC'),
                to: accountScopedRoute('ixc_invoice_notification_reports'),
              },
            ]
          : []),
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Connections',
      label: 'Conexões',
      icon: 'i-lucide-cable',
      activeOn: [
        'settings_connections_home',
        'settings_inbox_list',
        'settings_inbox_show',
        'settings_inbox_new',
        'settings_inbox_finish',
        'settings_inboxes_page_channel',
        'settings_inboxes_add_agents',
        'agent_bots',
        'settings_applications',
        'settings_integrations_dashboard_apps',
        'settings_integrations_webhook',
        'settings_integrations_slack',
        'settings_integrations_linear',
        'settings_integrations_notion',
        'settings_integrations_shopify',
        'settings_integrations_google_calendar',
        'settings_integrations_banking',
        'settings_integrations_erp',
        'settings_applications_integration',
      ],
      to: accountScopedRoute('settings_connections_home'),
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-settings',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-building-2',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-user-round',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users-round',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-ph-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-ph-user-gear',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-list-plus',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-refresh-cw',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Agent Availability',
          label: 'Status de disponibilidade',
          icon: 'i-lucide-coffee',
          to: accountScopedRoute('agent_pauses_settings'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-puzzle',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-text',
          to: accountScopedRoute('canned_list'),
        },
        ...(hasWhatsappCloudInbox.value
          ? [
              {
                name: 'Settings Interactive Messages',
                label: t('SIDEBAR.INTERACTIVE_MESSAGES'),
                icon: 'i-lucide-message-square-more',
                to: accountScopedRoute('whatsapp_interactive_templates'),
              },
            ]
          : []),
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-git-branch',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-ph-clock-countdown',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-ph-clipboard-text',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-ph-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings API Docs',
          label: t('SIDEBAR.API_DOCS'),
          icon: 'i-lucide-file-code-2',
          to: accountScopedRoute('api_docs_settings_index'),
        },
      ],
    },
    {
      name: 'Financeiro',
      label: 'Financeiro',
      icon: 'i-lucide-receipt-text',
      activeOn: ['billing_settings_index'],
      to: accountScopedRoute('billing_settings_index'),
      show: canAccessTalkviaShop.value,
    },
  ];
});

const favoriteItemNames = ref([]);
const collapsedSectionNames = ref([]);
const sectionOrderNames = ref([]);
const itemOrderNames = ref([]);
const isCustomizationOpen = ref(false);
const favoritesSectionLabel = 'Favoritos';

const sidebarCustomizationStorageKey = computed(
  () =>
    `talkvia:sidebar-customization:v1:${currentUserId.value || 'anonymous'}:${accountId.value || 'default'}`
);

const loadSidebarCustomization = () => {
  if (typeof window === 'undefined') return;

  try {
    const savedState = JSON.parse(
      window.localStorage.getItem(sidebarCustomizationStorageKey.value) || '{}'
    );
    favoriteItemNames.value = Array.isArray(savedState.favorites)
      ? uniqueSidebarNames(savedState.favorites)
      : [];
    collapsedSectionNames.value = Array.isArray(savedState.collapsedSections)
      ? uniqueSidebarNames(savedState.collapsedSections).filter(
          name => name !== 'section-favorites'
        )
      : [];
    sectionOrderNames.value = uniqueSidebarNames(savedState.sectionOrder);
    itemOrderNames.value = uniqueSidebarNames(savedState.itemOrder);
  } catch {
    favoriteItemNames.value = [];
    collapsedSectionNames.value = [];
    sectionOrderNames.value = [];
    itemOrderNames.value = [];
  }
};

const saveSidebarCustomization = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      sidebarCustomizationStorageKey.value,
      JSON.stringify({
        favorites: favoriteItemNames.value,
        collapsedSections: collapsedSectionNames.value,
        sectionOrder: sectionOrderNames.value,
        itemOrder: itemOrderNames.value,
      })
    );
  } catch {
    // The menu remains usable when storage is disabled or full.
  }
};

const applySidebarCustomization = ({
  favorites,
  collapsedSections,
  sectionOrder,
  itemOrder,
}) => {
  favoriteItemNames.value = uniqueSidebarNames(favorites);
  collapsedSectionNames.value = uniqueSidebarNames(collapsedSections).filter(
    name => name !== 'section-favorites'
  );
  sectionOrderNames.value = uniqueSidebarNames(sectionOrder);
  itemOrderNames.value = uniqueSidebarNames(itemOrder);
  saveSidebarCustomization();
  isCustomizationOpen.value = false;
};

const toggleSection = sectionName => {
  collapsedSectionNames.value = collapsedSectionNames.value.includes(
    sectionName
  )
    ? collapsedSectionNames.value.filter(name => name !== sectionName)
    : [...collapsedSectionNames.value, sectionName];
  saveSidebarCustomization();
};

const isSectionCollapsed = sectionName =>
  collapsedSectionNames.value.includes(sectionName);

const menuSections = computed(() => {
  const sections = [];
  let currentSection;

  menuItems.value.forEach(item => {
    if (item.type === 'section') {
      currentSection = { ...item, items: [] };
      sections.push(currentSection);
      return;
    }

    if (!currentSection) {
      currentSection = {
        type: 'section',
        name: 'section-menu',
        label: '',
        items: [],
      };
      sections.push(currentSection);
    }
    if (item.show !== false) currentSection.items.push(item);
  });

  return sections.filter(section => section.items.length);
});

const favoriteItems = computed(() => {
  return resolveFavoriteMenuItems(menuItems.value, favoriteItemNames.value);
});

const orderedMenuSections = computed(() => {
  const sections = orderSidebarSections(
    menuSections.value,
    sectionOrderNames.value,
    itemOrderNames.value
  );
  const primarySection = sections.find(
    section => section.name === 'section-menu'
  );
  if (!primarySection) return sections;

  return [
    {
      ...primarySection,
      items: [...primarySection.items].sort((first, second) => {
        if (first.name === 'Home') return -1;
        if (second.name === 'Home') return 1;
        return 0;
      }),
    },
    ...sections.filter(section => section.name !== 'section-menu'),
  ];
});

const regularMenuSections = computed(() =>
  excludeFavoriteMenuItems(orderedMenuSections.value, favoriteItems.value)
);

watch(sidebarCustomizationStorageKey, loadSidebarCustomization, {
  immediate: true,
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'flex-col justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <div
          class="flex min-w-0 flex-1 items-center gap-2"
          :class="{ 'justify-center': isEffectivelyCollapsed }"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand text-sm font-bold text-white shadow-sm shadow-n-brand/20"
          >
            {{ talkviaBrand.shortName }}
          </span>
          <span v-if="!isEffectivelyCollapsed" class="min-w-0 leading-tight">
            <strong class="block truncate text-sm font-bold text-n-slate-12">
              {{ talkviaBrand.name }}
            </strong>
            <small class="block truncate text-[10px] text-n-slate-10">
              {{ talkviaBrand.tagline }}
            </small>
          </span>
        </div>
        <button
          v-tooltip.right="isEffectivelyCollapsed ? t('SIDEBAR.EXPAND') : null"
          type="button"
          class="hidden size-8 shrink-0 place-content-center rounded-lg text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12 md:grid"
          :title="isEffectivelyCollapsed ? undefined : t('SIDEBAR.COLLAPSE')"
          :aria-label="
            isEffectivelyCollapsed ? t('SIDEBAR.EXPAND') : t('SIDEBAR.COLLAPSE')
          "
          @click="toggleSidebarCollapse"
        >
          <span
            class="size-4"
            :class="
              isEffectivelyCollapsed
                ? 'i-lucide-panel-left-open'
                : 'i-lucide-panel-left-close'
            "
          />
        </button>
      </div>
      <div
        class="flex min-w-0 items-center gap-1 px-2"
        :class="{ 'justify-center': isEffectivelyCollapsed }"
      >
        <SidebarAccountSwitcher
          :is-collapsed="isEffectivelyCollapsed"
          :class="
            isEffectivelyCollapsed
              ? ''
              : 'min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-1 shadow-sm'
          "
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
        <button
          v-if="!isEffectivelyCollapsed"
          v-tooltip.bottom="t('SIDEBAR.CUSTOMIZE.TITLE')"
          type="button"
          class="grid size-9 shrink-0 place-content-center rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-10 shadow-sm transition hover:border-n-brand/30 hover:text-n-brand"
          :title="t('SIDEBAR.CUSTOMIZE.TITLE')"
          :aria-label="t('SIDEBAR.CUSTOMIZE.TITLE')"
          @click="isCustomizationOpen = true"
        >
          <span class="i-lucide-layout-dashboard size-4" />
        </button>
      </div>
      <div
        class="grid min-w-0"
        :class="
          isEffectivelyCollapsed
            ? 'grid-cols-1 justify-items-center gap-2'
            : 'grid-cols-[2.25rem_minmax(0,1fr)] mx-2 items-center overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm'
        "
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex size-9 items-center justify-center border-r border-n-weak text-n-slate-11 transition-all duration-150 ease-out hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4" />
        </RouterLink>
        <RouterLink
          v-else
          v-tooltip.right="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          :to="{ name: 'search' }"
          class="flex size-9 items-center justify-center border border-n-weak bg-n-alpha-1 text-n-slate-11 shadow-sm transition-all duration-150 ease-out hover:border-n-brand/50 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand dark:hover:bg-n-slate-9/30"
          :aria-label="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4" />
        </RouterLink>
        <ComposeConversation align="start" trigger-class="min-w-0">
          <template #trigger="{ isOpen }">
            <Button
              v-tooltip.right="isEffectivelyCollapsed ? 'Nova conversa' : null"
              icon="i-lucide-message-square-plus"
              :label="isEffectivelyCollapsed ? '' : 'Nova conversa'"
              color="slate"
              size="md"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-9 !rounded-none !border !border-n-weak !bg-n-alpha-1 !p-0 !text-n-slate-11 !shadow-sm hover:!border-n-brand/50 hover:!bg-n-alpha-2'
                  : '!h-9 !w-full !min-w-0 !justify-center !rounded-none !border-0 !bg-transparent !px-2 !text-xs !font-medium !text-n-slate-12 !shadow-none hover:!bg-n-alpha-2',
                {
                  '!border-n-brand !bg-n-alpha-2 dark:!bg-n-slate-9/30':
                    isOpen && isEffectivelyCollapsed,
                  '!bg-n-alpha-2': isOpen && !isEffectivelyCollapsed,
                },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="sidebar-scroll-region grid min-w-0 flex-grow gap-2 overflow-y-scroll pb-5"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <li
          v-if="favoriteItems.length"
          class="sidebar-favorites-sticky z-30 list-none bg-n-background pb-2"
          :class="isEffectivelyCollapsed ? 'pt-0' : 'pt-1'"
        >
          <SidebarGroupSeparator
            v-if="!isEffectivelyCollapsed"
            :label="favoritesSectionLabel"
            class="rounded-lg bg-n-alpha-1"
          />
          <ul
            class="mt-1 flex min-w-0 list-none flex-col gap-1 p-0"
            :class="{ 'items-center': isEffectivelyCollapsed }"
          >
            <SidebarGroup
              v-for="item in favoriteItems"
              :key="`favorite-${item.name}`"
              v-bind="item"
              :class="{ 'ms-2 me-1': !isEffectivelyCollapsed }"
            />
          </ul>
        </li>
        <template v-for="section in regularMenuSections" :key="section.name">
          <li
            v-if="!isEffectivelyCollapsed && section.label"
            class="list-none pt-3 first:pt-1"
          >
            <SidebarGroupSeparator
              :label="section.label"
              collapsible
              class="rounded-lg bg-n-alpha-1"
              :is-expanded="!isSectionCollapsed(section.name)"
              @toggle="toggleSection(section.name)"
            />
          </li>
          <SidebarGroup
            v-for="item in section.items"
            v-show="!isSectionCollapsed(section.name)"
            :key="item.name"
            v-bind="item"
            :class="{
              'sticky top-0 z-20 bg-n-background pb-1':
                item.name === 'Conversation',
              'ms-2 me-1': section.label && !isEffectivelyCollapsed,
            }"
          />
        </template>
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnTalkviaCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnTalkviaCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <div :class="isEffectivelyCollapsed ? 'shrink-0' : 'min-w-0 flex-1'">
          <SidebarProfileMenu
            :is-collapsed="isEffectivelyCollapsed"
            @open-key-shortcut-modal="emit('openKeyShortcutModal')"
          />
        </div>
        <router-link
          v-if="!isEffectivelyCollapsed"
          v-tooltip.left="t('SIDEBAR_ITEMS.PROFILE_SETTINGS')"
          :to="{ name: 'profile_settings_index' }"
          class="grid size-9 shrink-0 place-content-center rounded-lg border border-transparent text-n-slate-10 transition hover:border-n-weak hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          :title="t('SIDEBAR_ITEMS.PROFILE_SETTINGS')"
          :aria-label="t('SIDEBAR_ITEMS.PROFILE_SETTINGS')"
          data-testid="sidebar-profile-settings"
          @click="closeMobileSidebar"
        >
          <span class="i-lucide-settings size-4.5" />
        </router-link>
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    <SidebarCustomizationDialog
      :open="isCustomizationOpen"
      :sections="orderedMenuSections"
      :favorite-names="favoriteItemNames"
      :collapsed-section-names="collapsedSectionNames"
      @close="isCustomizationOpen = false"
      @save="applySidebarCustomization"
    />
  </aside>
</template>

<style scoped>
.sidebar-scroll-region {
  scrollbar-color: rgba(120, 124, 136, 0.55) transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.sidebar-favorites-sticky {
  position: sticky;
  top: 0;
  box-shadow: 0 8px 12px -12px rgb(0 0 0 / 35%);
}

.sidebar-scroll-region::-webkit-scrollbar {
  width: 6px;
}

.sidebar-scroll-region::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-scroll-region::-webkit-scrollbar-thumb {
  background: rgba(120, 124, 136, 0.55);
  border-radius: 999px;
}

.sidebar-scroll-region::-webkit-scrollbar-thumb:hover {
  background: rgba(120, 124, 136, 0.8);
}
</style>
